// useDashboardData.ts — Sprint 14. Owns the dashboard's heavy
// initial-load effect plus the condo-switch flow. The orchestrator
// no longer touches useCondoStore for these mutations; it consumes
// the loading flag + stats + actions exposed here.
//
// Why this file is bigger than a typical store wrapper: the load
// effect coordinates SIX parallel store fetches + a derived stats
// snapshot, and the dependency-array footgun
// (currentCondo object reference vs. id) is documented on the
// effect's body. Bundling all of that here keeps the orchestrator
// free of those landmines.
import { useEffect, useState } from 'react';
import { useCondoStore } from '../../../store/condoStore';
import { useAuthStore }  from '../../../store/authStore';

export interface DashboardStats {
  totalOwners:         number;
  totalRevenue:        number;
  totalExpenses:       number;
  netIncome:           number;
  pendingFees:         number;
  overdueFees:         number;
  feeCollectionRate:   number;
  occupancyRate:       number;
  maintenanceRequests: number;
  openWorkOrders:      number;
  activeViolations:    number;
}

const EMPTY_STATS: DashboardStats = {
  totalOwners:         0,
  totalRevenue:        0,
  totalExpenses:       0,
  netIncome:           0,
  pendingFees:         0,
  overdueFees:         0,
  feeCollectionRate:   0,
  occupancyRate:       0,
  maintenanceRequests: 0,
  openWorkOrders:      0,
  activeViolations:    0,
};

// Sprint 144 (R14-5/R14-28, ADR-139) — this hook used to take
// `{ user, profileForm, settingsForm }` and seed both modal forms inside its
// load effect. All three are gone: the Account Settings modal was deleted (no
// endpoint behind any of its controls) and the profile form is now prefilled at
// OPEN time by the orchestrator from the live store user, so it can never show
// the pre-save name or the old hardcoded '+1 (555) 123-4567' placeholder phone.
// `user` was only ever read to derive those form values, so the hook now takes
// no arguments at all.

export interface UseDashboardData {
  loading:    boolean;
  stats:      DashboardStats;
  /** Switch to a different condo and re-fetch the per-condo collections.
   *  Sets `loading` while the parallel fetches are in flight. */
  switchTo:   (condoId: string) => Promise<void>;
}

export function useDashboardData(): UseDashboardData {
  const {
    getDashboardStats,
    initializeSampleData,
    fetchOwners, fetchFees, fetchPayments, fetchCondos,
    fetchViolations, fetchWorkOrders,
  } = useCondoStore();

  const {
    switchCondo, fetchAvailableCondos, currentCondo,
    availableCondos, setCurrentCondo,
  } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [stats,   setStats]   = useState<DashboardStats>(EMPTY_STATS);

  // Sprint 81 — BUG-5. An ADMIN with no bound property (condoId: null,
  // e.g. firm manager not yet pinned to a condo) used to land on the
  // dashboard with currentCondo === null. Every management page keys
  // off currentCondo?.id, so nav appeared to "do nothing" — each screen
  // rendered empty and bounced the manager back to the overview. The
  // login/fetchAvailableCondos auto-select can miss this when the first
  // /condos/available at login returns empty (then succeeds on the
  // dashboard load). This effect closes that gap: once availableCondos
  // resolves, if there's still no active property, auto-select the first
  // one — mirroring how super-admins already get Cedar Pointe on login.
  // Owners are unaffected (single condo, already selected); the empty
  // (no condos) case is surfaced by DashboardHeader's empty state.
  useEffect(() => {
    if (!currentCondo && availableCondos.length > 0) {
      setCurrentCondo(availableCondos[0]);
    }
  }, [currentCondo, availableCondos, setCurrentCondo]);

  // IMPORTANT: depend on `currentCondo?.id`, not the `currentCondo` object.
  // Zustand returns a fresh object reference on every store update, so every
  // call to fetchAvailableCondos() (which this effect makes) would change
  // the reference and re-trigger the effect — producing an infinite
  // /condos/available loop that left the dashboard stuck on a spinner.
  const currentCondoId = currentCondo?.id;

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        await fetchAvailableCondos();
        initializeSampleData(currentCondoId);
        await new Promise(resolve => setTimeout(resolve, 800));

        await Promise.all([
          fetchCondos(),
          fetchOwners(currentCondoId),
          fetchFees(currentCondoId),
          fetchPayments(currentCondoId),
          fetchWorkOrders(currentCondoId),
          fetchViolations(currentCondoId),
        ]);

        const dashboardStats = getDashboardStats(currentCondoId);
        // Sprint 30 — finding F15. The two stats below used to count the
        // FULL workOrders / violations arrays in the store, ignoring the
        // currentCondoId filter that every other stat respects. The
        // round-2 e2e sweep caught this as "Dashboard shows Open Work
        // Orders 1 / Active Violations 3 even when no property is
        // selected", while drilling into the feature pages showed 0 —
        // because the feature pages DO respect the filter. Now both
        // stats apply the same condo filter as getDashboardStats(),
        // and short-circuit to 0 when no condo is selected (admin
        // without binding, fresh login, etc.) so every KPI on the
        // overview agrees with the corresponding feature page.
        const allWorkOrders = useCondoStore.getState().workOrders;
        const allViolations = useCondoStore.getState().violations;
        const openWO = currentCondoId
          ? allWorkOrders.filter((w: any) =>
              w.condoId === currentCondoId
              && w.status !== 'completed' && w.status !== 'cancelled').length
          : 0;
        const activeV = currentCondoId
          ? allViolations.filter((v: any) =>
              v.condoId === currentCondoId
              && (v.status === 'open' || v.status === 'pending')).length
          : 0;
        setStats({ ...dashboardStats, openWorkOrders: openWO, activeViolations: activeV });

      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCondoId]);

  // Sprint 147 (ADR-142, R14-16) — switchTo used to `switchCondo(condoId)` and
  // THEN run its own Promise.all of the five per-condo fetches. But
  // `switchCondo` sets `currentCondo`, which changes `currentCondoId`, which
  // re-runs the load effect above — which fetches the same five collections.
  // So every property switch issued each request TWICE (plus condos +
  // available), in the production bundle where StrictMode double-invoke does
  // not apply. Correct data, 2× load, and the store's stale-response guard
  // (94.4) silently discarded whichever landed first.
  //
  // The effect is the right owner: it is keyed on the condo id, so it re-fires
  // on ANY route into a new condo, not just the switcher. switchTo therefore
  // only announces the new condo. Re-selecting the CURRENT condo does not
  // change the key and so would not refetch — that one case still refreshes
  // explicitly, preserving the "click my own property to reload" behaviour.
  //
  // Verified by a regression test that makes the mocked switchCondo actually
  // move currentCondo (the previous test mocked it inert, which is precisely
  // why this doubling was invisible to the suite).
  const switchTo = async (condoId: string) => {
    const wasAlreadyActive = currentCondoId === condoId;
    await switchCondo(condoId);
    if (!wasAlreadyActive) return; // the [currentCondoId] effect reloads

    try {
      setLoading(true);
      await Promise.all([
        fetchOwners(condoId),
        fetchFees(condoId),
        fetchPayments(condoId),
        fetchWorkOrders(condoId),
        fetchViolations(condoId),
      ]);
    } finally {
      setLoading(false);
    }
  };

  return { loading, stats, switchTo };
}
